/**
 * API client functions for demo dashboard operations.
 * @module lib/api
 *
 * @description
 * Provides functions for:
 * - Demo CRUD operations via Supabase
 * - Webhook calls to n8n for demo lifecycle
 * - Phone pool management
 * - Dashboard statistics
 *
 * All functions throw on error - callers should handle appropriately.
 */

import { supabase } from './supabase';
import { WEBHOOK_CREATE_DEMO, WEBHOOK_RELEASE_DEMO, WEBHOOK_DELETE_VAPI_ASSISTANT, WEBHOOK_PHONE_POOL_SYNC } from './constants';
import type {
  Demo,
  DemoNote,
  DemoFollowUp,
  DemoCall,
  DemoWithRelations,
  PhonePoolEntry,
  CreateDemoRequest,
  CreateDemoResponse,
  ReleaseDemoResponse,
  PhonePoolSummary,
  DashboardStats,
  SalesDashboardStats,
  CreateNoteRequest,
  UpdateNoteRequest,
  CreateFollowUpRequest,
  UpdateFollowUpRequest,
  CompleteFollowUpRequest,
  UpdateDemoSalesRequest,
  UpdateDemoFieldsRequest,
  DemoFilters,
  DemoSort,
  PipelineStage,
} from '@/types';

// ============ Demo Operations ============

/**
 * Fetches all active demos from the database.
 *
 * @description
 * Retrieves all demos with status='active', ordered by expiration date (ascending).
 * This is useful for displaying demos that are currently in use and showing which
 * ones will expire soonest.
 *
 * @returns {Promise<Demo[]>} Array of active demos sorted by expires_at (earliest first)
 *
 * @throws {Error} If database query fails or connection issues occur
 *
 * @example
 * ```typescript
 * try {
 *   const activeDemos = await fetchActiveDemos();
 *   console.log(`Found ${activeDemos.length} active demos`);
 *   activeDemos.forEach(demo => {
 *     console.log(`${demo.business_name} expires at ${demo.expires_at}`);
 *   });
 * } catch (error) {
 *   console.error('Failed to fetch active demos:', error);
 * }
 * ```
 */
export async function fetchActiveDemos(): Promise<Demo[]> {
  const { data, error } = await supabase
    .from('demos')
    .select('*')
    .eq('status', 'active')
    .order('expires_at', { ascending: true });

  if (error) throw new Error(error.message);
  return data as Demo[];
}

/**
 * Fetches all demos from the database with pagination support.
 *
 * @description
 * Retrieves all demos regardless of status, ordered by creation date (descending).
 * Most recently created demos appear first. Useful for displaying a complete
 * demo history or admin dashboard.
 *
 * @param {number} [limit=50] - Maximum number of demos to return. Defaults to 50.
 *
 * @returns {Promise<Demo[]>} Array of demos sorted by created_at (newest first)
 *
 * @throws {Error} If database query fails or connection issues occur
 *
 * @example
 * ```typescript
 * // Fetch last 50 demos
 * const recentDemos = await fetchAllDemos();
 *
 * // Fetch last 100 demos
 * const moreDemos = await fetchAllDemos(100);
 *
 * // Display demo history
 * recentDemos.forEach(demo => {
 *   console.log(`${demo.business_name} (${demo.status}) - ${demo.created_at}`);
 * });
 * ```
 */
export async function fetchAllDemos(limit = 50): Promise<Demo[]> {
  const { data, error } = await supabase
    .from('demos')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);
  return data as Demo[];
}

/**
 * Fetches all released/completed demos for the history view.
 *
 * @description
 * Retrieves demos with status='released', ordered by release date (descending).
 * These are demos that have been completed - either manually killed or expired
 * and had their phone numbers released back to the pool.
 *
 * @param {number} [limit=100] - Maximum number of demos to return. Defaults to 100.
 *
 * @returns {Promise<Demo[]>} Array of released demos sorted by released_at (newest first)
 *
 * @throws {Error} If database query fails or connection issues occur
 */
export async function fetchReleasedDemos(limit = 100): Promise<Demo[]> {
  const { data, error } = await supabase
    .from('demos')
    .select('*')
    .eq('status', 'released')
    .order('released_at', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (error) throw new Error(error.message);
  return data as Demo[];
}

/**
 * Fetches a single demo by its unique identifier.
 *
 * @description
 * Retrieves a specific demo using its ID. Returns null if the demo doesn't exist,
 * making it safe to use without try-catch for not-found scenarios. Other errors
 * will still throw.
 *
 * @param {string} id - The unique identifier of the demo to fetch
 *
 * @returns {Promise<Demo | null>} The demo object if found, null if not found
 *
 * @throws {Error} If database query fails (except for not-found errors which return null)
 *
 * @example
 * ```typescript
 * const demoId = '123e4567-e89b-12d3-a456-426614174000';
 *
 * const demo = await fetchDemoById(demoId);
 * if (demo) {
 *   console.log(`Found demo: ${demo.business_name}`);
 *   console.log(`Status: ${demo.status}`);
 *   console.log(`Assigned phone: ${demo.assigned_phone_number}`);
 * } else {
 *   console.log('Demo not found');
 * }
 * ```
 */
export async function fetchDemoById(id: string): Promise<Demo | null> {
  const { data, error } = await supabase
    .from('demos')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(error.message);
  }
  return data as Demo;
}

/**
 * Creates a new demo by calling the n8n webhook endpoint.
 *
 * @description
 * Initiates demo creation via n8n workflow which handles:
 * - Assigning an available phone number from the pool
 * - Creating VAPI assistant configuration
 * - Inserting demo record into the database
 * - Setting up expiration timers
 *
 * The webhook is asynchronous - the demo may not be fully provisioned when this returns.
 *
 * @param {CreateDemoRequest} request - Demo creation request containing business details and configuration
 * @param {string} request.businessName - Name of the business for the demo
 * @param {string} request.region - Geographic region for phone number assignment (e.g., 'us', 'uk')
 * @param {number} request.duration - Demo duration in minutes
 * @param {Object} request.config - Additional configuration for the demo
 *
 * @returns {Promise<CreateDemoResponse>} Response containing demo ID, assigned phone, and status
 *
 * @throws {Error} If webhook call fails, returns non-200 status, or network error occurs
 *
 * @example
 * ```typescript
 * try {
 *   const response = await createDemo({
 *     businessName: 'Acme Corp',
 *     region: 'us',
 *     duration: 60,
 *     config: {
 *       industry: 'retail',
 *       features: ['voice', 'sms']
 *     }
 *   });
 *
 *   console.log(`Demo created with ID: ${response.demoId}`);
 *   console.log(`Assigned phone: ${response.phoneNumber}`);
 *   console.log(`Expires at: ${response.expiresAt}`);
 * } catch (error) {
 *   console.error('Failed to create demo:', error.message);
 * }
 * ```
 */
export async function createDemo(request: CreateDemoRequest): Promise<CreateDemoResponse> {
  return webhookCallWithRetry<CreateDemoResponse>(
    WEBHOOK_CREATE_DEMO,
    request,
    { timeoutMs: 45000, maxRetries: 1, retryDelayMs: 3000, operation: 'create demo' }
  );
}

/**
 * Production-grade webhook caller with timeout, retry, and detailed error handling.
 *
 * Features:
 * - AbortController timeout (prevents hanging requests)
 * - Single retry with delay (handles transient n8n webhook registration delays)
 * - Detailed error classification (network, timeout, empty response, parse error)
 * - Structured error messages for frontend toast display
 */
async function webhookCallWithRetry<T>(
  url: string,
  body: unknown,
  opts: { timeoutMs: number; maxRetries: number; retryDelayMs: number; operation: string }
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    if (attempt > 0) {
      await new Promise(resolve => setTimeout(resolve, opts.retryDelayMs));
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), opts.timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `Webhook returned HTTP ${response.status}`);
      }

      const text = await response.text();
      if (!text || text.trim() === '') {
        // Empty response — retry once (n8n known issue: 200 OK with empty body on internal errors)
        lastError = new Error(
          `Webhook returned empty response during ${opts.operation}. ` +
          `The operation may have partially succeeded — please check active demos.`
        );
        if (attempt < opts.maxRetries) continue;
        throw lastError;
      }

      try {
        return JSON.parse(text) as T;
      } catch {
        throw new Error(`Invalid JSON response from ${opts.operation} webhook: ${text.substring(0, 200)}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof DOMException && error.name === 'AbortError') {
        lastError = new Error(
          `${opts.operation} timed out after ${opts.timeoutMs / 1000}s. ` +
          `The workflow may still be processing — please check active demos in a moment.`
        );
        if (attempt < opts.maxRetries) continue;
        throw lastError;
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        lastError = new Error(
          `Network error during ${opts.operation}. ` +
          `Please check your connection and try again.`
        );
        if (attempt < opts.maxRetries) continue;
        throw lastError;
      }

      // Non-retryable errors (HTTP errors, parse errors) — throw immediately
      throw error;
    }
  }

  throw lastError || new Error(`Failed to ${opts.operation} after ${opts.maxRetries + 1} attempts`);
}

/**
 * Releases a demo by calling the n8n webhook endpoint.
 *
 * @description
 * Terminates an active demo via n8n workflow which handles:
 * - Updating demo status to 'released'
 * - Returning the phone number to the pool with cooldown status
 * - Cleaning up VAPI assistant configuration
 * - Logging the release activity
 *
 * This is typically called when a user manually ends a demo before expiration,
 * or when the demo expires and needs cleanup.
 *
 * @param {string} demoId - The unique identifier of the demo to release
 *
 * @returns {Promise<ReleaseDemoResponse>} Response confirming release status and updated demo state
 *
 * @throws {Error} If webhook call fails, demo not found, returns non-200 status, or network error occurs
 *
 * @example
 * ```typescript
 * try {
 *   const demoId = '123e4567-e89b-12d3-a456-426614174000';
 *   const response = await releaseDemo(demoId);
 *
 *   console.log(`Demo released: ${response.success}`);
 *   console.log(`Phone number returned to pool: ${response.phoneNumber}`);
 *   console.log(`New phone status: ${response.phoneStatus}`); // 'cooldown'
 * } catch (error) {
 *   console.error('Failed to release demo:', error.message);
 * }
 * ```
 */
export async function releaseDemo(demoId: string): Promise<ReleaseDemoResponse> {
  return webhookCallWithRetry<ReleaseDemoResponse>(
    WEBHOOK_RELEASE_DEMO,
    { demoId },
    { timeoutMs: 30000, maxRetries: 1, retryDelayMs: 2000, operation: 'release demo' }
  );
}

/**
 * Deletes a demo from the database.
 *
 * @description
 * Permanently removes a demo record from the database. Before deletion,
 * attempts to clean up the associated Vapi assistant if one exists.
 * This should be used with caution as it removes all associated data
 * including notes, follow-ups, and call records via CASCADE delete.
 *
 * @param {string} demoId - The unique identifier of the demo to delete
 *
 * @returns {Promise<void>} Resolves when demo is deleted
 *
 * @throws {Error} If database delete fails or demo not found
 */
export async function deleteDemo(demoId: string): Promise<void> {
  // First, fetch the demo to get the vapi_assistant_id
  const { data: demo, error: fetchError } = await supabase
    .from('demos')
    .select('vapi_assistant_id')
    .eq('id', demoId)
    .single();

  if (fetchError && fetchError.code !== 'PGRST116') {
    throw new Error(`Failed to fetch demo: ${fetchError.message}`);
  }

  // If demo has a Vapi assistant, delete it first
  if (demo?.vapi_assistant_id) {
    try {
      const response = await fetch(
        WEBHOOK_DELETE_VAPI_ASSISTANT,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assistantId: demo.vapi_assistant_id }),
        }
      );

      if (!response.ok) {
        // Log but don't fail - orphan cleanup will catch it
        if (import.meta.env.DEV) console.warn(
          `Failed to delete Vapi assistant ${demo.vapi_assistant_id}, will be cleaned up by orphan process`
        );
      }
    } catch (err) {
      // Log but don't fail - orphan cleanup will catch it
      if (import.meta.env.DEV) console.warn('Error calling delete assistant webhook:', err);
    }
  }

  // Delete the demo record
  const { error } = await supabase
    .from('demos')
    .delete()
    .eq('id', demoId);

  if (error) throw new Error(error.message);
}

// ============ Phone Pool Operations ============

/**
 * Fetches all phone numbers from the demo phone pool.
 *
 * @description
 * Retrieves the complete phone pool inventory with all phone numbers and their
 * current status. Results are ordered by region first, then by status, making
 * it easy to see regional distribution and availability.
 *
 * Phone statuses include:
 * - 'available': Ready to be assigned to a new demo
 * - 'assigned': Currently in use by an active demo
 * - 'cooldown': Recently released, waiting before reuse
 * - 'disabled': Manually disabled, not available for assignment
 *
 * @returns {Promise<PhonePoolEntry[]>} Array of all phone pool entries with status and metadata
 *
 * @throws {Error} If database query fails or connection issues occur
 *
 * @example
 * ```typescript
 * const phonePool = await fetchPhonePool();
 *
 * // Group by region
 * const byRegion = phonePool.reduce((acc, phone) => {
 *   if (!acc[phone.region]) acc[phone.region] = [];
 *   acc[phone.region].push(phone);
 *   return acc;
 * }, {});
 *
 * // Display availability
 * console.log('Phone Pool Status:');
 * phonePool.forEach(phone => {
 *   console.log(`${phone.phone_number} (${phone.region}): ${phone.status}`);
 *   if (phone.assigned_demo_id) {
 *     console.log(`  Assigned to demo: ${phone.assigned_demo_id}`);
 *   }
 * });
 * ```
 */
export async function fetchPhonePool(): Promise<PhonePoolEntry[]> {
  const { data, error } = await supabase
    .from('demo_phone_pool')
    .select('*')
    .order('region', { ascending: true })
    .order('status', { ascending: true });

  if (error) throw new Error(error.message);
  return data as PhonePoolEntry[];
}

/**
 * Fetches aggregated statistics about the phone pool.
 *
 * @description
 * Provides a high-level summary of phone pool status by counting phones in each
 * status category. This is more efficient than fetching all phones when you only
 * need counts for dashboard displays or capacity planning.
 *
 * The summary includes:
 * - available: Phones ready for immediate assignment
 * - assigned: Phones currently in use
 * - cooldown: Phones waiting to become available again
 * - disabled: Phones manually taken out of rotation
 * - total: Total number of phones in the pool
 *
 * @returns {Promise<PhonePoolSummary>} Object containing counts for each status category
 *
 * @throws {Error} If database query fails or connection issues occur
 *
 * @example
 * ```typescript
 * const summary = await fetchPhonePoolSummary();
 *
 * console.log('Phone Pool Summary:');
 * console.log(`Total phones: ${summary.total}`);
 * console.log(`Available: ${summary.available}`);
 * console.log(`Assigned: ${summary.assigned}`);
 * console.log(`In cooldown: ${summary.cooldown}`);
 * console.log(`Disabled: ${summary.disabled}`);
 *
 * // Check capacity
 * const utilizationRate = (summary.assigned / summary.total) * 100;
 * if (utilizationRate > 80) {
 *   console.warn('Phone pool is running low on capacity!');
 * }
 * ```
 */
export async function fetchPhonePoolSummary(): Promise<PhonePoolSummary> {
  const phones = await fetchPhonePool();

  return {
    available: phones.filter(p => p.status === 'available').length,
    assigned: phones.filter(p => p.status === 'assigned').length,
    cooldown: phones.filter(p => p.status === 'cooldown').length,
    disabled: phones.filter(p => p.status === 'disabled').length,
    total: phones.length,
  };
}

/**
 * Manually releases a phone from cooldown status back to available.
 *
 * @description
 * Allows sales team to manually skip the cooldown period and make a phone
 * immediately available for new demos. This is useful when:
 * - Phone pool is exhausted and demos are urgently needed
 * - Cooldown period is no longer necessary (e.g., demo was cancelled quickly)
 * - Testing or administrative purposes
 *
 * Only phones in 'cooldown' status can be released using this function.
 *
 * @param {string} phoneId - The UUID of the phone to release
 * @returns {Promise<PhonePoolEntry>} The updated phone entry
 *
 * @throws {Error} If phone is not found or not in cooldown status
 * @throws {Error} If database update fails
 *
 * @example
 * ```typescript
 * try {
 *   const phone = await releasePhoneFromCooldown('7d2f4a68-7781-4e4b-bd39-8d0d1a29f5f8');
 *   console.log(`Phone ${phone.phone_number} is now available`);
 * } catch (error) {
 *   console.error('Failed to release phone:', error.message);
 * }
 * ```
 */
export async function releasePhoneFromCooldown(phoneId: string): Promise<PhonePoolEntry> {
  // First verify the phone exists and is in cooldown
  const { data: phone, error: fetchError } = await supabase
    .from('demo_phone_pool')
    .select('*')
    .eq('id', phoneId)
    .single();

  if (fetchError || !phone) {
    throw new Error('Phone not found');
  }

  if (phone.status !== 'cooldown') {
    throw new Error(`Phone is not in cooldown status (current: ${phone.status})`);
  }

  // Update to available
  const { data: updated, error: updateError } = await supabase
    .from('demo_phone_pool')
    .update({
      status: 'available',
      cooldown_until: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', phoneId)
    .select()
    .single();

  if (updateError) throw new Error(updateError.message);
  return updated as PhonePoolEntry;
}

// ============ Phone Pool Sync (Vapi → Supabase) ============

export interface VapiSyncResult {
  missing: Array<{
    vapi_phone_id: string;
    phone_number: string;
    phone_number_formatted: string;
    area_code: string;
    region: string;
    city_hint: string | null;
  }>;
  orphaned: Array<{
    id: string;
    phone_number: string;
    phone_number_formatted: string | null;
    vapi_phone_id: string;
    region: string;
    status: string;
  }>;
  synced: number;
  totalVapi: number;
  totalPool: number;
}

export interface AddToPoolRequest {
  phone_number: string;
  phone_number_formatted: string;
  vapi_phone_id: string;
  region: string;
  area_code: string;
  city_hint: string | null;
}

/**
 * Fetches sync status between Vapi phone numbers and the demo_phone_pool.
 * Returns missing numbers (in Vapi but not pool), orphaned entries (in pool but not Vapi),
 * and counts for synced/total.
 */
export async function fetchVapiSyncStatus(): Promise<VapiSyncResult> {
  return webhookCallWithRetry<VapiSyncResult>(
    WEBHOOK_PHONE_POOL_SYNC,
    { action: 'list' },
    { timeoutMs: 15000, maxRetries: 0, retryDelayMs: 0, operation: 'fetch Vapi sync status' }
  );
}

/**
 * Adds missing phone numbers to the demo_phone_pool via n8n webhook.
 * Uses service role key to bypass RLS.
 */
export async function addNumbersToPool(numbers: AddToPoolRequest[]): Promise<{ success: boolean; added: number }> {
  return webhookCallWithRetry<{ success: boolean; added: number }>(
    WEBHOOK_PHONE_POOL_SYNC,
    { action: 'add', numbers },
    { timeoutMs: 15000, maxRetries: 0, retryDelayMs: 0, operation: 'add numbers to pool' }
  );
}

// ============ Dashboard Stats ============

/**
 * Fetches comprehensive statistics for the dashboard overview.
 *
 * @description
 * Aggregates multiple metrics in parallel for optimal performance:
 * - Current active demo count
 * - Available phones in the pool
 * - Demos created today (since midnight)
 * - Demos created this week (since Sunday)
 * - Demos expiring in the next 15 minutes (urgency indicator)
 *
 * All time-based calculations use the server's local timezone. Queries are
 * executed in parallel using Promise.all for maximum efficiency.
 *
 * @returns {Promise<DashboardStats>} Object containing all dashboard metrics
 *
 * @throws {Error} If any database query fails or connection issues occur
 *
 * @example
 * ```typescript
 * const stats = await fetchDashboardStats();
 *
 * console.log('Dashboard Overview:');
 * console.log(`Active Demos: ${stats.activeDemos}`);
 * console.log(`Available Phones: ${stats.availablePhones}`);
 * console.log(`Demos Created Today: ${stats.demosToday}`);
 * console.log(`Demos This Week: ${stats.demosThisWeek}`);
 *
 * // Alert for expiring demos
 * if (stats.expiringIn15Min > 0) {
 *   console.warn(`⚠️ ${stats.expiringIn15Min} demo(s) expiring soon!`);
 * }
 *
 * // Calculate daily vs weekly growth
 * const weeklyAvg = stats.demosThisWeek / 7;
 * console.log(`Daily average this week: ${weeklyAvg.toFixed(1)}`);
 * ```
 */
export async function fetchDashboardStats(): Promise<DashboardStats> {
  const now = new Date();
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);

  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const in15Min = new Date(now.getTime() + 15 * 60 * 1000);

  // Fetch all data in parallel
  const [activeDemos, demosToday, demosThisWeek, expiringDemos, poolSummary] = await Promise.all([
    supabase
      .from('demos')
      .select('id', { count: 'exact' })
      .eq('status', 'active'),
    supabase
      .from('demos')
      .select('id', { count: 'exact' })
      .gte('created_at', startOfDay.toISOString()),
    supabase
      .from('demos')
      .select('id', { count: 'exact' })
      .gte('created_at', startOfWeek.toISOString()),
    supabase
      .from('demos')
      .select('id', { count: 'exact' })
      .eq('status', 'active')
      .lte('expires_at', in15Min.toISOString()),
    fetchPhonePoolSummary(),
  ]);

  return {
    activeDemos: activeDemos.count || 0,
    availablePhones: poolSummary.available,
    demosToday: demosToday.count || 0,
    demosThisWeek: demosThisWeek.count || 0,
    expiringIn15Min: expiringDemos.count || 0,
  };
}

// ============ Activity Log ============

/**
 * Fetches recent activity log entries with associated demo information.
 *
 * @description
 * Retrieves the most recent activity events from the demo_activity_log table,
 * ordered by timestamp (newest first). Includes a join with the demos table
 * to provide business_name context for each activity.
 *
 * Activity types typically include:
 * - 'demo_created': New demo was provisioned
 * - 'demo_released': Demo was manually released
 * - 'demo_expired': Demo reached its expiration time
 * - 'phone_assigned': Phone number assigned to demo
 * - 'phone_released': Phone number returned to pool
 *
 * @param {number} [limit=10] - Maximum number of activity entries to return. Defaults to 10.
 *
 * @returns {Promise<Array>} Array of activity log entries with demo details
 * @returns {string} return[].id - Activity log entry ID
 * @returns {string} return[].demo_id - Associated demo ID
 * @returns {string} return[].action - Type of activity/action performed
 * @returns {string} return[].created_at - Timestamp of the activity
 * @returns {Object} return[].demos - Joined demo information
 * @returns {string} return[].demos.business_name - Business name from associated demo
 *
 * @throws {Error} If database query fails or connection issues occur
 *
 * @example
 * ```typescript
 * // Fetch last 10 activities
 * const recentActivity = await fetchRecentActivity();
 *
 * // Fetch last 25 activities
 * const moreActivity = await fetchRecentActivity(25);
 *
 * // Display activity feed
 * console.log('Recent Activity:');
 * recentActivity.forEach(activity => {
 *   const businessName = activity.demos?.business_name || 'Unknown';
 *   const timestamp = new Date(activity.created_at).toLocaleString();
 *   console.log(`[${timestamp}] ${activity.action} - ${businessName}`);
 * });
 *
 * // Filter by action type
 * const creations = recentActivity.filter(a => a.action === 'demo_created');
 * console.log(`${creations.length} demos created recently`);
 * ```
 */
export async function fetchRecentActivity(limit = 10) {
  const { data, error } = await supabase
    .from('demo_activity_log')
    .select(`
      *,
      demos (
        business_name
      )
    `)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);
  return data;
}

// ============ Sales Tracking Operations ============

/**
 * Fetches a demo with all related data (notes, follow-ups, calls, activities).
 */
export async function fetchDemoWithRelations(id: string): Promise<DemoWithRelations | null> {
  const { data, error } = await supabase
    .from('demos')
    .select(`
      *,
      demo_notes(*),
      follow_ups:demo_follow_ups(*),
      calls:demo_calls(*),
      activities:demo_activity_log(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(error.message);
  }

  // Sort related data
  if (data.demo_notes) {
    data.demo_notes.sort((a: DemoNote, b: DemoNote) => {
      // Pinned first, then by date
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }
  if (data.follow_ups) {
    data.follow_ups.sort((a: DemoFollowUp, b: DemoFollowUp) => {
      // Incomplete first, then by due date
      if (!a.is_completed && b.is_completed) return -1;
      if (a.is_completed && !b.is_completed) return 1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });
  }
  if (data.calls) {
    data.calls.sort((a: DemoCall, b: DemoCall) =>
      new Date(b.started_at || b.created_at).getTime() - new Date(a.started_at || a.created_at).getTime()
    );
  }
  if (data.activities) {
    data.activities.sort((a: { created_at: string }, b: { created_at: string }) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  return data as DemoWithRelations;
}

/**
 * Fetches demos with filters and sorting for sales dashboard.
 */
export async function fetchDemosWithFilters(
  filters: DemoFilters = {},
  sort: DemoSort = { field: 'last_activity_at', direction: 'desc' },
  limit = 50
): Promise<Demo[]> {
  let query = supabase.from('demos').select('*');

  // Apply filters
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      query = query.in('status', filters.status);
    } else {
      query = query.eq('status', filters.status);
    }
  }

  if (filters.pipeline_stage) {
    if (Array.isArray(filters.pipeline_stage)) {
      query = query.in('pipeline_stage', filters.pipeline_stage);
    } else {
      query = query.eq('pipeline_stage', filters.pipeline_stage);
    }
  }

  if (filters.lead_source) {
    query = query.eq('lead_source', filters.lead_source);
  }

  if (filters.industry) {
    query = query.eq('industry', filters.industry);
  }

  if (filters.region) {
    query = query.eq('region', filters.region);
  }

  if (filters.sales_agent_email) {
    query = query.eq('sales_agent_email', filters.sales_agent_email);
  }

  if (filters.min_score !== undefined) {
    query = query.gte('lead_score', filters.min_score);
  }

  if (filters.max_score !== undefined) {
    query = query.lte('lead_score', filters.max_score);
  }

  if (filters.has_calls !== undefined) {
    if (filters.has_calls) {
      query = query.gt('calls_received', 0);
    } else {
      query = query.eq('calls_received', 0);
    }
  }

  if (filters.search) {
    query = query.or(
      `business_name.ilike.%${filters.search}%,contact_name.ilike.%${filters.search}%,contact_email.ilike.%${filters.search}%`
    );
  }

  // Apply sorting
  query = query.order(sort.field, { ascending: sort.direction === 'asc' });

  // Apply limit
  query = query.limit(limit);

  const { data, error } = await query;

  if (error) throw new Error(error.message);
  return data as Demo[];
}

/**
 * Updates demo sales tracking fields.
 */
export async function updateDemoSales(
  demoId: string,
  updates: UpdateDemoSalesRequest
): Promise<Demo> {
  const { data, error } = await supabase
    .from('demos')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', demoId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as Demo;
}

/**
 * Updates demo business/content fields (services, prompt, website data, etc.).
 */
export async function updateDemoFields(
  demoId: string,
  updates: UpdateDemoFieldsRequest
): Promise<Demo> {
  const { data, error } = await supabase
    .from('demos')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', demoId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  if (import.meta.env.DEV) {
    console.log('[updateDemoFields] Updated:', demoId, 'keys:', Object.keys(updates));
  }
  return data as Demo;
}

/**
 * Updates just the pipeline stage of a demo.
 */
export async function updateDemoPipelineStage(
  demoId: string,
  stage: PipelineStage
): Promise<Demo> {
  const { data, error } = await supabase
    .from('demos')
    .update({
      pipeline_stage: stage,
      updated_at: new Date().toISOString(),
    })
    .eq('id', demoId)
    .select()
    .single();

  if (error) throw new Error(error.message);

  // Log the stage change
  await supabase.from('demo_activity_log').insert({
    demo_id: demoId,
    event_type: 'stage_changed',
    event_data: { new_stage: stage },
  });

  return data as Demo;
}

// ============ Notes Operations ============

/**
 * Fetches all notes for a demo.
 */
export async function fetchDemoNotes(demoId: string): Promise<DemoNote[]> {
  const { data, error } = await supabase
    .from('demo_notes')
    .select('*')
    .eq('demo_id', demoId)
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data as DemoNote[];
}

/**
 * Creates a new note for a demo.
 */
export async function createNote(request: CreateNoteRequest): Promise<DemoNote> {
  const { data, error } = await supabase
    .from('demo_notes')
    .insert({
      demo_id: request.demo_id,
      content: request.content,
      category: request.category || 'general',
      created_by: request.created_by,
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as DemoNote;
}

/**
 * Updates an existing note.
 */
export async function updateNote(
  noteId: string,
  updates: UpdateNoteRequest
): Promise<DemoNote> {
  const { data, error } = await supabase
    .from('demo_notes')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', noteId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as DemoNote;
}

/**
 * Toggles the pinned status of a note.
 */
export async function toggleNotePin(noteId: string, isPinned: boolean): Promise<DemoNote> {
  return updateNote(noteId, { is_pinned: isPinned });
}

/**
 * Deletes a note.
 */
export async function deleteNote(noteId: string): Promise<void> {
  const { error } = await supabase
    .from('demo_notes')
    .delete()
    .eq('id', noteId);

  if (error) throw new Error(error.message);
}

// ============ Follow-up Operations ============

/**
 * Fetches all follow-ups for a demo.
 */
export async function fetchDemoFollowUps(demoId: string): Promise<DemoFollowUp[]> {
  const { data, error } = await supabase
    .from('demo_follow_ups')
    .select('*')
    .eq('demo_id', demoId)
    .order('is_completed', { ascending: true })
    .order('due_date', { ascending: true });

  if (error) throw new Error(error.message);
  return data as DemoFollowUp[];
}

/**
 * Fetches all pending follow-ups across all demos.
 */
export async function fetchAllPendingFollowUps(): Promise<(DemoFollowUp & { demos: { business_name: string } })[]> {
  const { data, error } = await supabase
    .from('demo_follow_ups')
    .select(`
      *,
      demos (
        business_name
      )
    `)
    .eq('is_completed', false)
    .order('due_date', { ascending: true });

  if (error) throw new Error(error.message);
  return data as (DemoFollowUp & { demos: { business_name: string } })[];
}

/**
 * Fetches overdue follow-ups.
 */
export async function fetchOverdueFollowUps(): Promise<(DemoFollowUp & { demos: { business_name: string } })[]> {
  const { data, error } = await supabase
    .from('demo_follow_ups')
    .select(`
      *,
      demos (
        business_name
      )
    `)
    .eq('is_completed', false)
    .lt('due_date', new Date().toISOString())
    .order('due_date', { ascending: true });

  if (error) throw new Error(error.message);
  return data as (DemoFollowUp & { demos: { business_name: string } })[];
}

/**
 * Creates a new follow-up task.
 */
export async function createFollowUp(request: CreateFollowUpRequest): Promise<DemoFollowUp> {
  const { data, error } = await supabase
    .from('demo_follow_ups')
    .insert({
      demo_id: request.demo_id,
      title: request.title,
      due_date: request.due_date,
      task_type: request.task_type || 'call',
      description: request.description,
      priority: request.priority || 'medium',
      reminder_at: request.reminder_at,
      created_by: request.created_by,
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as DemoFollowUp;
}

/**
 * Updates an existing follow-up.
 */
export async function updateFollowUp(
  followUpId: string,
  updates: UpdateFollowUpRequest
): Promise<DemoFollowUp> {
  const { data, error } = await supabase
    .from('demo_follow_ups')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', followUpId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as DemoFollowUp;
}

/**
 * Marks a follow-up as completed.
 */
export async function completeFollowUp(
  followUpId: string,
  request: CompleteFollowUpRequest = {}
): Promise<DemoFollowUp> {
  const { data, error } = await supabase
    .from('demo_follow_ups')
    .update({
      is_completed: true,
      completed_at: new Date().toISOString(),
      completed_by: request.completed_by,
      completion_notes: request.completion_notes,
      updated_at: new Date().toISOString(),
    })
    .eq('id', followUpId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as DemoFollowUp;
}

/**
 * Deletes a follow-up.
 */
export async function deleteFollowUp(followUpId: string): Promise<void> {
  const { error } = await supabase
    .from('demo_follow_ups')
    .delete()
    .eq('id', followUpId);

  if (error) throw new Error(error.message);
}

// ============ Call Operations ============

/**
 * Fetches all calls for a demo.
 */
export async function fetchDemoCalls(demoId: string): Promise<DemoCall[]> {
  const { data, error } = await supabase
    .from('demo_calls')
    .select('*')
    .eq('demo_id', demoId)
    .order('started_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data as DemoCall[];
}

// ============ Sales Dashboard Stats ============

/**
 * Fetches comprehensive sales dashboard statistics.
 */
export async function fetchSalesDashboardStats(): Promise<SalesDashboardStats> {
  const now = new Date();
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  // Fetch all required data in parallel
  const [
    allDemos,
    followUpsDueToday,
    overdueFollowUps,
    wonThisMonth,
    lostThisMonth,
  ] = await Promise.all([
    // Active demos only (exclude expired, released, and closed pipeline stages)
    supabase
      .from('demos')
      .select('lead_score, pipeline_stage, calls_received, estimated_monthly_value, close_probability')
      .eq('status', 'active')
      .not('pipeline_stage', 'in', '(won,lost)'),

    // Follow-ups due today
    supabase
      .from('demo_follow_ups')
      .select('id', { count: 'exact' })
      .eq('is_completed', false)
      .gte('due_date', startOfDay.toISOString())
      .lt('due_date', new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000).toISOString()),

    // Overdue follow-ups
    supabase
      .from('demo_follow_ups')
      .select('id', { count: 'exact' })
      .eq('is_completed', false)
      .lt('due_date', now.toISOString()),

    // Won this month
    supabase
      .from('demos')
      .select('id', { count: 'exact' })
      .eq('pipeline_stage', 'won')
      .gte('updated_at', startOfMonth.toISOString()),

    // Lost this month
    supabase
      .from('demos')
      .select('id', { count: 'exact' })
      .eq('pipeline_stage', 'lost')
      .gte('updated_at', startOfMonth.toISOString()),
  ]);

  const demos = allDemos.data || [];

  // Calculate lead score distribution
  const hotLeads = demos.filter(d => d.lead_score >= 70).length;
  const warmLeads = demos.filter(d => d.lead_score >= 40 && d.lead_score < 70).length;
  const coldLeads = demos.filter(d => d.lead_score < 40).length;

  // Calculate demos called today
  const demosWithCalls = demos.filter(d => d.calls_received > 0).length;
  const demoToCallRate = demos.length > 0 ? (demosWithCalls / demos.length) * 100 : 0;

  // Calculate pipeline value
  const pipelineValue = demos
    .filter(d => d.estimated_monthly_value && d.close_probability)
    .reduce((sum, d) => sum + (d.estimated_monthly_value * (d.close_probability / 100)), 0);

  // Count by stage
  const byStage = demos.reduce((acc, d) => {
    const stage = d.pipeline_stage as PipelineStage;
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {} as Record<PipelineStage, number>);

  // Ensure all stages have a value
  const allStages: PipelineStage[] = [
    'new_lead', 'demo_sent', 'demo_engaged', 'follow_up', 'interested',
    'proposal_sent', 'negotiating', 'won', 'lost', 'nurture'
  ];
  allStages.forEach(stage => {
    if (!(stage in byStage)) byStage[stage] = 0;
  });

  return {
    totalLeads: demos.length,
    hotLeads,
    warmLeads,
    coldLeads,
    demosCalledToday: demosWithCalls, // This should actually check last_call_at for today
    followUpsDueToday: followUpsDueToday.count || 0,
    overdueFollowUps: overdueFollowUps.count || 0,
    demoToCallRate: Math.round(demoToCallRate * 10) / 10,
    wonThisMonth: wonThisMonth.count || 0,
    lostThisMonth: lostThisMonth.count || 0,
    pipelineValue: Math.round(pipelineValue),
    byStage,
  };
}

// ============ Lead Scoring ============

/**
 * Recalculates lead scores for all active demos.
 * Triggers the database function that updates scores based on engagement.
 */
export async function recalculateAllLeadScores(): Promise<number> {
  const { data, error } = await supabase.rpc('recalculate_all_lead_scores');

  if (error) {
    if (import.meta.env.DEV) console.error('Error recalculating lead scores:', error);
    throw new Error('Failed to recalculate lead scores');
  }

  return data as number;
}

/**
 * Score breakdown interface for displaying score components.
 */
export interface LeadScoreBreakdown {
  total: number;
  callEngagement: number;
  pipelineStage: number;
  recency: number;
  contactCompleteness: number;
  dealProbability: number;
}

/**
 * Calculates a detailed breakdown of a lead's score.
 * This mirrors the database function logic for frontend display.
 */
export function calculateScoreBreakdown(demo: Demo): LeadScoreBreakdown {
  let callEngagement = 0;
  let pipelineStage = 0;
  let recency = 0;
  let contactCompleteness = 0;
  let dealProbability = 0;

  // 1. Call Engagement (max 35)
  if (demo.calls_received >= 5) callEngagement = 20;
  else if (demo.calls_received >= 3) callEngagement = 15;
  else if (demo.calls_received >= 2) callEngagement = 10;
  else if (demo.calls_received === 1) callEngagement = 5;

  if (demo.calls_received > 0) {
    const avgDuration = demo.total_call_duration_seconds / demo.calls_received;
    if (avgDuration >= 180) callEngagement += 15;
    else if (avgDuration >= 120) callEngagement += 10;
    else if (avgDuration >= 60) callEngagement += 5;
  }

  // 2. Pipeline Stage (max 25)
  const stageScores: Record<PipelineStage, number> = {
    won: 25,
    negotiating: 22,
    proposal_sent: 20,
    interested: 18,
    demo_engaged: 15,
    follow_up: 12,
    demo_sent: 8,
    new_lead: 5,
    nurture: 3,
    lost: 0,
  };
  pipelineStage = stageScores[demo.pipeline_stage] ?? 5;

  // 3. Recency (max 20)
  if (demo.last_activity_at) {
    const daysSince = (Date.now() - new Date(demo.last_activity_at).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince <= 1) recency = 20;
    else if (daysSince <= 3) recency = 15;
    else if (daysSince <= 7) recency = 10;
    else if (daysSince <= 14) recency = 5;
  } else {
    recency = 5;
  }

  // 4. Contact Completeness (max 10)
  if (demo.contact_name) contactCompleteness += 3;
  if (demo.contact_email) contactCompleteness += 3;
  if (demo.contact_phone) contactCompleteness += 2;
  if (demo.estimated_monthly_value && demo.estimated_monthly_value > 0) contactCompleteness += 2;

  // 5. Deal Probability (max 10)
  if (demo.close_probability) {
    dealProbability = Math.round(demo.close_probability * 10);
  }

  const total = Math.min(callEngagement + pipelineStage + recency + contactCompleteness + dealProbability, 100);

  return {
    total,
    callEngagement,
    pipelineStage,
    recency,
    contactCompleteness,
    dealProbability,
  };
}

// ============ Demo Conversion ============

/**
 * Response from demo conversion
 */
export interface ConvertDemoResponse {
  success: boolean;
  tenantId: string;
  tenantSlug: string;
  message: string;
}

/**
 * Converts a demo to a tenant (paying customer).
 * This creates a new tenant record and marks the demo as converted.
 *
 * DEMO-P0-1 FIX: Now also releases the assigned phone to cooldown status.
 */
export async function convertDemoToTenant(
  demoId: string,
  subscriptionStatus: 'trial' | 'active' = 'trial'
): Promise<ConvertDemoResponse> {
  const { data, error } = await supabase.rpc('convert_demo_to_tenant', {
    p_demo_id: demoId,
    p_subscription_status: subscriptionStatus,
  });

  if (error) {
    if (import.meta.env.DEV) console.error('Error converting demo:', error);
    throw new Error(error.message || 'Failed to convert demo to tenant');
  }

  // DEMO-P0-1 FIX: Release the phone back to pool with 24-hour cooldown
  // This prevents phone pool exhaustion after demo conversions
  try {
    const cooldownUntil = new Date();
    cooldownUntil.setHours(cooldownUntil.getHours() + 24);

    const { error: phoneError } = await supabase
      .from('demo_phone_pool')
      .update({
        status: 'cooldown',
        assigned_demo_id: null,
        cooldown_until: cooldownUntil.toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('assigned_demo_id', demoId);

    if (phoneError) {
      // Log but don't fail conversion - phone release is best effort
      if (import.meta.env.DEV) console.warn('DEMO-P0-1: Failed to release phone after conversion:', phoneError);
    } else {
      if (import.meta.env.DEV) console.log(`DEMO-P0-1: Phone released to cooldown for demo ${demoId}`);
    }
  } catch (phoneErr) {
    if (import.meta.env.DEV) console.warn('DEMO-P0-1: Phone release error (non-fatal):', phoneErr);
  }

  // Fetch the created tenant to get the slug
  const { data: tenant } = await supabase
    .from('tenants')
    .select('id, slug')
    .eq('id', data)
    .single();

  return {
    success: true,
    tenantId: data as string,
    tenantSlug: tenant?.slug || '',
    message: 'Demo successfully converted to customer',
  };
}

/**
 * Demos that are ready for conversion (high engagement, won stage, etc.)
 */
export interface ConversionReadyDemo {
  id: string;
  business_name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  pipeline_stage: PipelineStage;
  lead_score: number;
  calls_received: number;
  estimated_monthly_value: number | null;
  city: string | null;
  region: string;
  industry: string;
  bookings_made: number;
  high_intent_calls: number;
  last_activity_at: string;
  conversion_recommendation: string;
}

/**
 * Fetches demos that are ready for conversion.
 */
export async function fetchConversionReadyDemos(): Promise<ConversionReadyDemo[]> {
  const { data, error } = await supabase
    .from('conversion_ready_demos')
    .select('*');

  if (error) {
    if (import.meta.env.DEV) console.error('Error fetching conversion ready demos:', error);
    throw new Error('Failed to fetch conversion ready demos');
  }

  return data as ConversionReadyDemo[];
}

// ============ Call Analysis ============

/**
 * Extended call data with analysis fields
 */
export interface DemoCallWithAnalysis extends DemoCall {
  key_topics: string[];
  objections_raised: string[];
  action_items: string[];
  interest_signals: string[];
  buying_intent: 'high' | 'medium' | 'low' | 'none' | null;
  next_steps: string | null;
  analyzed_at: string | null;
}

/**
 * Call analysis summary for dashboard
 */
export interface CallAnalysisSummary {
  call_id: string;
  demo_id: string;
  business_name: string;
  pipeline_stage: string;
  caller_phone: string | null;
  duration_seconds: number;
  call_status: string;
  sentiment_score: number | null;
  booking_made: boolean;
  ai_summary: string | null;
  key_topics: string[];
  objections_raised: string[];
  action_items: string[];
  interest_signals: string[];
  buying_intent: string | null;
  next_steps: string | null;
  call_date: string;
  analyzed_at: string | null;
  call_outcome_summary: string;
  objection_count: number | null;
  action_item_count: number | null;
}

/**
 * Fetches call analysis data for a demo.
 */
export async function fetchDemoCallAnalysis(demoId: string): Promise<CallAnalysisSummary[]> {
  const { data, error } = await supabase
    .from('demo_call_analysis')
    .select('*')
    .eq('demo_id', demoId)
    .order('call_date', { ascending: false });

  if (error) {
    if (import.meta.env.DEV) console.error('Error fetching call analysis:', error);
    throw new Error('Failed to fetch call analysis');
  }

  return data as CallAnalysisSummary[];
}

/**
 * Fetches recent call analysis across all demos.
 */
export async function fetchRecentCallAnalysis(limit = 20): Promise<CallAnalysisSummary[]> {
  const { data, error } = await supabase
    .from('demo_call_analysis')
    .select('*')
    .order('call_date', { ascending: false })
    .limit(limit);

  if (error) {
    if (import.meta.env.DEV) console.error('Error fetching recent call analysis:', error);
    throw new Error('Failed to fetch recent call analysis');
  }

  return data as CallAnalysisSummary[];
}
